import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { APP_ROUTES } from '@/common/constants'
import { Button } from '@/common/ui/button'
import PieChartWithLabel from '@/common/ui/PieChartWithLabel'
import { BUNDLE_COPY } from '@/locales/en'
import { SecurityAvatar } from '@/securities/securityAvatar'
import { ArrowRightIcon } from 'lucide-react'
import { useRef, forwardRef, useImperativeHandle } from 'react'
import { useNavigate } from 'react-router'

const SECURITY_COLOR_PALETTE = ['#137157', '#0F766E', '#059669']

function colorForIndex(i: number) {
    return SECURITY_COLOR_PALETTE[i % SECURITY_COLOR_PALETTE.length]
}

type Props = {
    bundleId?: string
    securities?: {
        securityCode: string
        name: string
        weight: number
        summary: string
    }[]
}

export type BundleBreakdownHandle = {
    navigateToSecurity: (securityCode: string) => void
}

export const BundleBreakdownLoading: React.FC = () => (
    <div className="w-full flex flex-col justify-start items-start gap-6">
        <div className="self-stretch inline-flex justify-start items-center gap-2.5">
            <div className="h-6 bg-stone-100 rounded w-1/3 animate-pulse" />
        </div>
        <div className="w-full flex items-center justify-center">
            <div className="w-40 h-40 rounded-full bg-stone-100 animate-pulse" />
        </div>
        <div className="self-stretch w-full flex flex-col justify-start items-start gap-3">
            <div className="h-4 bg-stone-100 rounded w-3/4 mb-2 animate-pulse" />
            <div className="h-3 bg-stone-100 rounded w-full mb-2 animate-pulse" />
            <div className="h-3 bg-stone-100 rounded w-5/6 animate-pulse" />
        </div>
    </div>
)

const SecurityBreakdownItem: React.FC<{
    securityCode: string
    name: string
    summary: string
    color: string
    percentLabel: string
    aboutRefs?: React.MutableRefObject<Record<string, HTMLDivElement | null>>
    onClick: (securityCode: string) => void
}> = ({ securityCode, name, summary, color, percentLabel, aboutRefs, onClick }) => {
    return (
        <div
            key={securityCode}
            ref={(el) => {
                if (aboutRefs) {
                    aboutRefs.current[securityCode] = el
                }
            }}
            className="self-stretch p-4 bg-stone-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-border-outline inline-flex flex-col justify-start items-start gap-2 overflow-hidden"
        >
            <div className="w-full self-stretch inline-flex justify-start items-center gap-6">
                <div className="self-stretch inline-flex justify-start items-center gap-4">
                    <div className="w-4 aspect-square rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    <SecurityAvatar securityCode={securityCode} height={8} width={8} />
                    <div className="justify-start text-content-primary text-base font-medium font-['Inter'] leading-6 flex items-center">
                        {securityCode}
                    </div>
                </div>
                <div className="flex-1 text-right justify-start text-base font-medium font-['Inter'] leading-6">
                    {percentLabel}
                </div>
            </div>
            <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
                <div className="self-stretch justify-start text-left text-content-secondary text-sm font-normal font-['Inter'] leading-6 line-clamp-1">
                    {name}
                </div>
                <div className="self-stretch justify-start text-content-primary text-sm font-normal font-['Inter'] leading-5">
                    {summary}
                </div>
                <Button
                    variant={'link'}
                    className="px-0 py-0 text-black text-sm font-medium font-['Inter'] underline hover:no-underline"
                    onClick={() => onClick(securityCode)}
                >
                    {BUNDLE_COPY.learnMore}
                    <ArrowRightIcon color="black" className="ml-1 h-4 w-4" />
                </Button>
            </div>
        </div>
    )
}

export const BundleBreakdown = forwardRef<BundleBreakdownHandle, Props>(({ securities, bundleId }, ref) => {
    const navigate = useNavigate()

    const aboutAccordionRefs = useRef<Record<string, HTMLDivElement | null>>({})

    const onNavigateToSecurity = (securityCode: string) => {
        const el = aboutAccordionRefs.current[securityCode]
        if (el) {
            const offset = 80 // px to account for header
            const top = window.scrollY + el.getBoundingClientRect().top - offset
            window.scrollTo({ top, behavior: 'smooth' })
        }
    }

    useImperativeHandle(ref, () => ({
        navigateToSecurity: (securityCode: string) => onNavigateToSecurity(securityCode),
    }))

    if (!bundleId || !securities || securities.length === 0) {
        return <BundleBreakdownLoading />
    }

    const enrichedSecurities = securities.map((s, i) => ({
        ...s,
        color: colorForIndex(i),
        percentLabel: `${s.weight}%`,
    }))
    const pieData = enrichedSecurities.map((s) => ({ name: s.securityCode, value: s.weight ?? 0, color: s.color }))

    const handleClick = (securityCode: string) => {
        analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_SECURITY_CLICKED, {
            security_code: securityCode,
            bundle_id: bundleId,
        })
        navigate(`${APP_ROUTES.SECURITY}/${securityCode}`)
    }

    return (
        <div className="w-full flex flex-col justify-start items-start gap-6">
            <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                    {BUNDLE_COPY.breakdown}
                </div>
            </div>
            <div className="w-50 h-50 w-full justify-center items-center gap-2.5 inline-flex">
                <PieChartWithLabel data={pieData} height={200} isAnimationActive={false} />
            </div>
            <div className="self-stretch w-full flex flex-col justify-start items-start">
                <div className="w-full flex flex-col gap-6">
                    {enrichedSecurities.map(({ securityCode, summary, color, percentLabel, name }) => (
                        <SecurityBreakdownItem
                            key={securityCode}
                            securityCode={securityCode}
                            summary={summary}
                            color={color}
                            percentLabel={percentLabel}
                            name={name}
                            aboutRefs={aboutAccordionRefs}
                            onClick={handleClick}
                        />
                    ))}
                </div>
            </div>
            <div className="self-stretch justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                {BUNDLE_COPY.aboutDisclaimer}
            </div>
        </div>
    )
})
